/**
 * input.js — one "hand" abstraction over XR controllers and mouse/keyboard.
 *
 * The rest of the game never asks whether it is running in a headset. It asks
 * `input.getHand('right')` and reads a pose, a grip value and a thumbstick.
 * That is what lets Tin Can Alley be genuinely playable on both.
 *
 * Desktop throw model: holding the grab button suppresses mouse-look and turns
 * mouse motion into hand motion in camera space. Flicking the mouse therefore
 * moves the hand through world space for real, and the same ring-buffer
 * velocity estimator that serves VR produces the throw. One code path, two
 * input devices.
 *
 * @module core/input
 */

import * as THREE from 'three';
import { HAND, AVATAR, EVENTS } from '../constants.js';
import { bus } from './events.js';
import { xr } from './xr.js';

/** Desktop mouse-to-hand gain. Tuned so a brisk flick lands near 10 m/s. */
const MOUSE_THROW_GAIN = 0.0135;
/** How fast the desktop hand springs back to rest when not gesturing. */
const HAND_RETURN_RATE = 9;
/** Clamp on how far the desktop hand may travel from rest, metres. */
const HAND_MAX_OFFSET = 0.55;

/**
 * A hand — an XR controller or the desktop stand-in.
 * Poses are refreshed once per frame in world space.
 */
export class Hand {
  /** @param {'left'|'right'} side */
  constructor(side) {
    this.side = side;
    /** Whether a real tracked source is driving this hand. */
    this.active = false;
    /** The node whose world transform is the hand pose. Held objects attach here. */
    /** @type {THREE.Object3D|null} */
    this.object3D = null;

    this.position = new THREE.Vector3();
    this.quaternion = new THREE.Quaternion();
    /** Forward direction of the hand (−Z of its frame). */
    this.direction = new THREE.Vector3(0, 0, -1);
    /** Smoothed world velocity, m/s. Informational — throws use grab.js's estimator. */
    this.velocity = new THREE.Vector3();

    this.grip = 0;
    this.trigger = 0;
    this.gripPressed = false;
    this.triggerPressed = false;
    this.gripJustPressed = false;
    this.gripJustReleased = false;
    this.triggerJustPressed = false;
    this.triggerJustReleased = false;

    this.thumbstick = { x: 0, y: 0 };
    this.buttons = { a: false, b: false, thumbstick: false };

    this._prevPos = new THREE.Vector3();
    this._hasPrev = false;
  }

  /** @returns {boolean} True on the frame the grip closed. */
  get grabStarted() { return this.gripJustPressed; }
  /** @returns {boolean} True on the frame the grip opened. */
  get grabEnded() { return this.gripJustReleased; }
}

class InputManager {
  constructor() {
    /** @type {{left: Hand, right: Hand}} */
    this.hands = { left: new Hand('left'), right: new Hand('right') };
    /** Desktop only: which hand the mouse currently drives (keys 1 / 2). */
    this.activeSide = 'right';
    this.isVR = false;

    /** Locomotion intent, −1..1. Desktop WASD or left thumbstick. */
    this.moveAxis = { x: 0, y: 0 };
    /** Turn intent, −1..1. Desktop arrow keys or right thumbstick X. */
    this.turnAxis = 0;

    this.pointerLocked = false;
    this.enabled = true;

    /** @type {Set<string>} */ this._keys = new Set();
    /** @type {Set<string>} */ this._actionsThisFrame = new Set();
    this._mouseDelta = { x: 0, y: 0 };
    this._lookDelta = { x: 0, y: 0 };
    this._mouseDown = false;
    this._desktopGrabHeld = false;

    /** Camera-space offset of the desktop hand, driven by mouse gestures. */
    this._handOffset = new THREE.Vector3();

    this._camera = null;
    this._canvas = null;
  }

  /**
   * @param {THREE.PerspectiveCamera} camera
   * @param {HTMLCanvasElement} canvas
   */
  init(camera, canvas) {
    this._camera = camera;
    this._canvas = canvas;

    // Desktop hands live under the camera so they inherit head motion.
    for (const side of ['left', 'right']) {
      const node = new THREE.Group();
      node.name = `desktop-hand-${side}`;
      const rest = HAND.desktopOffset;
      node.position.set(side === 'left' ? -rest.x : rest.x, rest.y, rest.z);
      camera.add(node);
      this.hands[side].object3D = node;
    }

    this._bindKeyboard();
    this._bindMouse(canvas);

    bus.on(EVENTS.XR_SESSION_START, () => this._enterVR());
    bus.on(EVENTS.XR_SESSION_END, () => this._exitVR());
  }

  /**
   * @param {'left'|'right'} side
   * @returns {Hand}
   */
  getHand(side) {
    return this.hands[side] || this.hands.right;
  }

  /** @returns {Hand} The hand the desktop mouse is currently driving. */
  getActiveHand() {
    return this.hands[this.activeSide];
  }

  /**
   * Was an action triggered this frame?
   * Actions: 'grab-left','grab-right','activate','teleport','exit','pause'
   * @param {string} name
   * @returns {boolean}
   */
  wasPressed(name) {
    return this._actionsThisFrame.has(name);
  }

  /** @param {string} code KeyboardEvent.code @returns {boolean} */
  isKeyDown(code) {
    return this._keys.has(code);
  }

  /** Per-frame poll. Called by the engine before gameplay updaters. */
  update(dt) {
    this._actionsThisFrame.clear();
    if (!this.enabled) return;

    if (this.isVR) this._updateVR(dt);
    else this._updateDesktop(dt);

    for (const side of ['left', 'right']) this._updatePose(this.hands[side], dt);

    // Consume this frame's mouse motion.
    this._lookDelta.x = 0;
    this._lookDelta.y = 0;
    this._mouseDelta.x = 0;
    this._mouseDelta.y = 0;
  }

  // ── VR path ───────────────────────────────────────────────────────────────

  /** @private */
  _updateVR(dt) {
    for (const side of ['left', 'right']) {
      const hand = this.hands[side];
      const grip = xr.getGrip(side);
      hand.object3D = grip || hand.object3D;
      const pad = xr.getGamepad(side);
      hand.active = !!pad;
      if (!pad) {
        this._setButton(hand, 'grip', 0);
        this._setButton(hand, 'trigger', 0);
        continue;
      }
      // WebXR standard gamepad mapping: 0 = trigger, 1 = squeeze,
      // 3 = thumbstick press, 4/5 = A/B (or X/Y on the left controller).
      const trigger = pad.buttons[0] ? pad.buttons[0].value : 0;
      // Some runtimes report squeeze at index 4 instead of 1. Fall back only
      // when 1 is genuinely absent, so A/X never doubles as a grab button on
      // controllers that follow the standard mapping.
      const squeeze = pad.buttons[1]
        ? pad.buttons[1].value
        : (pad.buttons[4] ? pad.buttons[4].value : 0);
      this._setButton(hand, 'trigger', trigger);
      this._setButton(hand, 'grip', squeeze);
      hand.buttons.thumbstick = !!(pad.buttons[3] && pad.buttons[3].pressed);
      hand.buttons.a = !!(pad.buttons[4] && pad.buttons[4].pressed);
      hand.buttons.b = !!(pad.buttons[5] && pad.buttons[5].pressed);

      const ax = pad.axes || [];
      // Axes 2/3 are the thumbstick on Quest controllers; 0/1 on some others.
      const sx = ax.length >= 4 ? ax[2] : (ax[0] || 0);
      const sy = ax.length >= 4 ? ax[3] : (ax[1] || 0);
      const dead = 0.12;
      hand.thumbstick.x = Math.abs(sx) > dead ? sx : 0;
      hand.thumbstick.y = Math.abs(sy) > dead ? sy : 0;
      if (hand.thumbstick.x || hand.thumbstick.y) {
        bus.emit(EVENTS.INPUT_THUMBSTICK, { side, x: hand.thumbstick.x, y: hand.thumbstick.y });
      }
    }

    // Left stick moves (hub only — teleport.js gates VR locomotion per GDD 12.3),
    // right stick turns.
    const l = this.hands.left;
    const r = this.hands.right;
    this.moveAxis.x = l.thumbstick.x;
    this.moveAxis.y = -l.thumbstick.y;
    this.turnAxis = r.thumbstick.x;
  }

  // ── Desktop path ──────────────────────────────────────────────────────────

  /** @private */
  _updateDesktop(dt) {
    const fwd = (this._keys.has('KeyW') ? 1 : 0) - (this._keys.has('KeyS') ? 1 : 0);
    const strafe = (this._keys.has('KeyD') ? 1 : 0) - (this._keys.has('KeyA') ? 1 : 0);
    this.moveAxis.x = strafe;
    this.moveAxis.y = fwd;
    this.turnAxis = (this._keys.has('ArrowRight') ? 1 : 0) - (this._keys.has('ArrowLeft') ? 1 : 0);

    const active = this.hands[this.activeSide];
    const other = this.hands[this.activeSide === 'left' ? 'right' : 'left'];
    active.active = true;
    other.active = false;
    this._setButton(other, 'grip', 0);
    this._setButton(other, 'trigger', 0);

    const wantGrab = this._mouseDown || this._keys.has('KeyE');
    this._setButton(active, 'grip', wantGrab ? 1 : 0);
    this._setButton(active, 'trigger', this._keys.has('KeyF') ? 1 : 0);
    this._desktopGrabHeld = wantGrab;

    // While grabbing, mouse motion drives the hand instead of the camera.
    if (wantGrab) {
      this._handOffset.x += this._mouseDelta.x * MOUSE_THROW_GAIN;
      this._handOffset.y += -this._mouseDelta.y * MOUSE_THROW_GAIN * 0.6;
      this._handOffset.z += -Math.max(0, -this._mouseDelta.y) * MOUSE_THROW_GAIN * 1.5;
      this._handOffset.clampLength(0, HAND_MAX_OFFSET);
    } else {
      this._handOffset.multiplyScalar(Math.max(0, 1 - HAND_RETURN_RATE * dt));
      if (this._handOffset.lengthSq() < 1e-6) this._handOffset.set(0, 0, 0);
    }

    const rest = HAND.desktopOffset;
    const node = active.object3D;
    if (node) {
      node.position.set(
        (this.activeSide === 'left' ? -rest.x : rest.x) + this._handOffset.x,
        rest.y + this._handOffset.y,
        rest.z + this._handOffset.z,
      );
    }
  }

  /**
   * Mouse-look delta for the avatar controller to consume, in radians.
   * Returns zeroes while a grab gesture owns the mouse.
   * @returns {{x: number, y: number}}
   */
  consumeLook() {
    if (this._desktopGrabHeld) return { x: 0, y: 0 };
    return { x: this._lookDelta.x, y: this._lookDelta.y };
  }

  // ── Button edge tracking ──────────────────────────────────────────────────

  /**
   * @param {Hand} hand
   * @param {'grip'|'trigger'} which
   * @param {number} value 0–1
   * @private
   */
  _setButton(hand, which, value) {
    const threshold = 0.55;
    const wasPressed = which === 'grip' ? hand.gripPressed : hand.triggerPressed;
    const nowPressed = value >= threshold;

    if (which === 'grip') {
      hand.grip = value;
      hand.gripPressed = nowPressed;
      hand.gripJustPressed = nowPressed && !wasPressed;
      hand.gripJustReleased = !nowPressed && wasPressed;
      if (hand.gripJustPressed) {
        this._actionsThisFrame.add(`grab-${hand.side}`);
        bus.emit(EVENTS.INPUT_GRIP_DOWN, { side: hand.side });
      }
      if (hand.gripJustReleased) bus.emit(EVENTS.INPUT_GRIP_UP, { side: hand.side });
    } else {
      hand.trigger = value;
      hand.triggerPressed = nowPressed;
      hand.triggerJustPressed = nowPressed && !wasPressed;
      hand.triggerJustReleased = !nowPressed && wasPressed;
      if (hand.triggerJustPressed) bus.emit(EVENTS.INPUT_TRIGGER_DOWN, { side: hand.side });
      if (hand.triggerJustReleased) bus.emit(EVENTS.INPUT_TRIGGER_UP, { side: hand.side });
    }
  }

  /**
   * Refresh a hand's world pose and derived velocity.
   * @param {Hand} hand
   * @param {number} dt
   * @private
   */
  _updatePose(hand, dt) {
    const node = hand.object3D;
    if (!node) return;
    node.updateWorldMatrix(true, false);
    node.getWorldPosition(hand.position);
    node.getWorldQuaternion(hand.quaternion);
    hand.direction.set(0, 0, -1).applyQuaternion(hand.quaternion);

    if (hand._hasPrev && dt > 0) {
      hand.velocity.subVectors(hand.position, hand._prevPos).divideScalar(dt);
    }
    hand._prevPos.copy(hand.position);
    hand._hasPrev = true;
  }

  // ── VR / desktop switching ────────────────────────────────────────────────

  /** @private */
  _enterVR() {
    this.isVR = true;
    for (const side of ['left', 'right']) {
      const grip = xr.getGrip(side);
      if (grip) this.hands[side].object3D = grip;
      this.hands[side]._hasPrev = false;
    }
    this._exitPointerLock();
  }

  /** @private */
  _exitVR() {
    this.isVR = false;
    for (const side of ['left', 'right']) {
      const node = this._camera.getObjectByName(`desktop-hand-${side}`);
      if (node) this.hands[side].object3D = node;
      this.hands[side]._hasPrev = false;
    }
  }

  // ── Event binding ─────────────────────────────────────────────────────────

  /** @private */
  _bindKeyboard() {
    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      this._keys.add(e.code);
      switch (e.code) {
        case 'Digit1': this.activeSide = 'left'; break;
        case 'Digit2': this.activeSide = 'right'; break;
        case 'KeyF': this._actionsThisFrame.add('activate'); bus.emit(EVENTS.INPUT_ACTION, { action: 'activate' }); break;
        case 'KeyT': this._actionsThisFrame.add('teleport'); bus.emit(EVENTS.INPUT_ACTION, { action: 'teleport' }); break;
        case 'Escape': this._actionsThisFrame.add('exit'); bus.emit(EVENTS.INPUT_ACTION, { action: 'exit' }); break;
        default: break;
      }
    });
    window.addEventListener('keyup', (e) => this._keys.delete(e.code));
    window.addEventListener('blur', () => {
      this._keys.clear();
      this._mouseDown = false;
    });
  }

  /** @private */
  _bindMouse(canvas) {
    if (!canvas) return;

    canvas.addEventListener('mousedown', (e) => {
      if (e.button === 0) this._mouseDown = true;
      // Pointer lock is a nicety; the game is fully playable without it, which
      // matters because headless Chromium will not grant it.
      if (!this.pointerLocked && !this.isVR && canvas.requestPointerLock) {
        canvas.requestPointerLock();
      }
    });
    window.addEventListener('mouseup', (e) => {
      if (e.button === 0) this._mouseDown = false;
    });
    window.addEventListener('mousemove', (e) => {
      const dx = e.movementX ?? 0;
      const dy = e.movementY ?? 0;
      this._mouseDelta.x += dx;
      this._mouseDelta.y += dy;
      if (!this._desktopGrabHeld) {
        this._lookDelta.x += dx * 0.0022;
        this._lookDelta.y += dy * 0.0022;
      }
    });
    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement === canvas;
    });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /** @private */
  _exitPointerLock() {
    if (document.pointerLockElement && document.exitPointerLock) document.exitPointerLock();
  }
}

/** The one input manager. */
export const input = new InputManager();
